import { useNode, useEditor, type Node } from '@craftjs/core';
import { useEffect, useCallback } from 'react';
import SharedPopup, { defaultProps, type PopupProps } from '@builder4/shared/components/Popup';
import { InspectorPopup } from '@/components/inspector/InspectorPopup/InspectorPopup';
import { isContainerNode, isPopupNode } from '@/utils/helperFunc';

const EditorPopup = (props: Partial<PopupProps> & { children?: React.ReactNode }) => {
  props = { ...defaultProps, ...props };

  const {
    id,
    selected,
    actions: { setProp },
  } = useNode((node) => ({
    id: node.id,
    selected: node.events.selected,
  }));

  const { actions, query } = useEditor();

  // When popup is selected, close all other popups and open this one
  useEffect(() => {
    if (selected) {
      const allNodes = query.getNodes();
      for (const [nodeId, node] of Object.entries(allNodes)) {
        if (isPopupNode(node) && nodeId !== id && node.data.props.isOpen) {
          actions.setProp(nodeId, (props: PopupProps) => {
            props.isOpen = false;
          });
        }
      }
      setProp((props: PopupProps) => {
        props.isOpen = true;
      });
    }
  }, [selected, setProp, actions, query, id]);

  // When overlay is clicked, deselect the popup so it can be re-selected to open again
  const handleClose = useCallback(() => {
    actions.selectNode(undefined);
  }, [actions]);

  return (
    <SharedPopup {...props} onClose={handleClose}>
      {props.children}
    </SharedPopup>
  );
};

EditorPopup.craft = {
  ...SharedPopup.craft,
  rules: {
    canDrag: () => true,
    canMoveIn: (incomingNodes: Node[]) => {
      return incomingNodes.every((node) => isContainerNode(node));
    },
  },
  related: {
    toolbar: InspectorPopup,
  },
};

export default EditorPopup;
